import React, { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { getUsers, createUser, updateUser, deleteUser, resendInvite, adminSendReset } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Pencil, Users as UsersIcon, ShieldCheck, MailCheck, KeyRound, Send } from "lucide-react";
import { useT } from "@/i18n/I18nContext";

const ROLES = ["admin", "manager", "qa", "operator"];
const roleBadge = {
  admin: "bg-[hsl(var(--brand))] text-white",
  manager: "badge-ok",
  qa: "badge-warn",
  operator: "bg-secondary text-muted-foreground border border-border",
};

const empty = { id: null, email: "", name: "", role: "operator", password: "", send_invite: true };

export default function Users() {
  const { user: me } = useAuth();
  const { t } = useT();
  const [users, setUsers] = useState([]);
  const [dialog, setDialog] = useState(null);

  const load = async () => setUsers(await getUsers());
  useEffect(() => {
    (async () => { await load(); })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (f) => {
    try {
      if (f.id) {
        const payload = { name: f.name, role: f.role };
        if (f.password) payload.password = f.password;
        await updateUser(f.id, payload);
        toast.success(t("users.saved"));
      } else {
        if (!f.email || !f.name) {
          toast.error(t("users.emailNameRequired"));
          return;
        }
        if (!f.send_invite && !f.password) {
          toast.error(t("users.passwordRequired"));
          return;
        }
        const res = await createUser({
          email: f.email, name: f.name, role: f.role,
          send_invite: !!f.send_invite,
          ...(f.send_invite ? {} : { password: f.password }),
        });
        toast.success(res?.invited ? t("users.inviteSent") : t("users.saved"));
      }
      setDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("users.failedSave"));
    }
  };

  const doResendInvite = async (u) => {
    try {
      await resendInvite(u.id);
      toast.success(t("users.inviteResent"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("users.failedSave"));
    }
  };

  const doSendReset = async (u) => {
    try {
      await adminSendReset(u.id);
      toast.success(t("users.resetSent"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("users.failedSave"));
    }
  };

  const remove = async (id) => {
    try {
      await deleteUser(id);
      toast.success(t("users.removed"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("users.failedDelete"));
    }
  };

  return (
    <Layout
      actions={
        <Button size="sm" onClick={() => setDialog({ ...empty })} data-testid="add-user-button">
          <Plus className="mr-1.5 h-4 w-4" /> {t("users.addUser")}
        </Button>
      }
    >
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("users.title")} ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center">
              <UsersIcon className="h-10 w-10 text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">{t("users.empty")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto thin-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("users.name")}</TableHead>
                    <TableHead>{t("users.email")}</TableHead>
                    <TableHead>{t("users.role")}</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell className="font-medium">
                        {u.name} {me?.id === u.id && <span className="text-xs text-muted-foreground">{t("users.you")}</span>}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        <div className="flex items-center gap-2">
                          {u.email}
                          {u.email_verified === false && (
                            <Badge className="badge-warn gap-1 text-[10px]" data-testid={`user-pending-${u.id}`}>
                              <MailCheck className="h-3 w-3" /> {t("users.pending")}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell><Badge className={roleBadge[u.role]}>{u.role}</Badge></TableCell>
                      <TableCell className="text-right">
                        {u.email_verified === false ? (
                          <Button variant="ghost" size="icon" title={t("users.resendInvite")} onClick={() => doResendInvite(u)} data-testid={`user-resend-${u.id}`}>
                            <Send className="h-4 w-4 text-[hsl(var(--brand))]" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="icon" title={t("users.sendReset")} onClick={() => doSendReset(u)} data-testid={`user-reset-${u.id}`}>
                            <KeyRound className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => setDialog({ ...u, password: "" })} data-testid={`user-edit-${u.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {me?.id !== u.id && (
                          <Button variant="ghost" size="icon" onClick={() => remove(u.id)} data-testid={`user-delete-${u.id}`}>
                            <Trash2 className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <UserDialog key={dialog ? (dialog.id ?? "new") : "closed"} dialog={dialog} onClose={() => setDialog(null)} onSave={save} />
    </Layout>
  );
}

function UserDialog({ dialog, onClose, onSave }) {
  const { t } = useT();
  const [f, setF] = useState(dialog);
  if (!f) return null;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{f.id ? t("users.editUser") : t("users.addUser")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("users.name")}</Label>
            <Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="user-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("users.email")}</Label>
            <Input type="email" value={f.email} disabled={!!f.id} onChange={(e) => set("email", e.target.value)} data-testid="user-email-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("users.role")}</Label>
            <Select value={f.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {!f.id && (
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
              <div className="pr-3">
                <div className="text-sm font-medium">{t("users.sendActivation")}</div>
                <div className="text-xs text-muted-foreground">{t("users.sendActivationHint")}</div>
              </div>
              <Switch checked={!!f.send_invite} onCheckedChange={(v) => set("send_invite", v)} data-testid="user-invite-switch" />
            </div>
          )}
          {(f.id || !f.send_invite) && (
            <div className="space-y-1.5">
              <Label>{f.id ? t("users.newPassword") : t("users.password")}</Label>
              <Input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} data-testid="user-password-input" />
              {f.id && <p className="text-xs text-muted-foreground">{t("users.leaveBlankKeep")}</p>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={() => onSave(f)} data-testid="user-save-button">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
